(() => {
  const nativeFetch = window.fetch.bind(window);
  const maximumWidgetOffset = 1000;

  const requestUrl = (input) => {
    try {
      return new URL(typeof input === 'string' || input instanceof URL ? input : input.url, window.location.href);
    } catch (error) {
      return null;
    }
  };

  const isJudgeMeReviewsRequest = (url) =>
    url &&
    (url.hostname === 'api.judge.me' || url.hostname === 'cdn.judge.me') &&
    url.pathname.endsWith('/reviews/reviews_for_widget');

  const fetchWithUrl = (url, input, init) => {
    if (input instanceof Request) {
      return nativeFetch(new Request(url.toString(), input), init);
    }
    return nativeFetch(url.toString(), init);
  };

  const responseWithJson = (response, data) => {
    const headers = new Headers(response.headers);
    headers.set('content-type', 'application/json; charset=utf-8');
    headers.delete('content-length');
    return new Response(JSON.stringify(data), {
      status: response.status,
      statusText: response.statusText,
      headers,
    });
  };

  window.fetch = async (input, init) => {
    const url = requestUrl(input);
    const requestedPage = Number.parseInt(url?.searchParams.get('page') || '1', 10);

    if (!isJudgeMeReviewsRequest(url) || !Number.isFinite(requestedPage) || requestedPage <= 200) {
      return nativeFetch(input, init);
    }

    const hasUnsupportedFilter = [
      'filter_rating',
      'filter_rating[]',
      'show_media_only',
      'search',
      'keyword',
      'cf_answer[]',
      'review_uuid',
      'other_language_page',
    ].some((parameter) => url.searchParams.has(parameter));

    if (hasUnsupportedFilter) return nativeFetch(input, init);

    const cappedResponse = await nativeFetch(input, init);
    if (!cappedResponse.ok) return cappedResponse;

    try {
      const data = await cappedResponse.clone().json();
      const pagination = data.primary_language_pagination || data.pagination;
      const reviewsKey = Array.isArray(data.primary_language_reviews) ? 'primary_language_reviews' : 'reviews';
      const totalPages = Number(pagination?.total_pages || 0);
      const reviewsPerPage = Number(pagination?.per_page || 0);
      const totalReviews = Number(data.number_of_reviews || 0);

      if (
        !Array.isArray(data[reviewsKey]) ||
        !Number.isFinite(totalPages) ||
        !Number.isFinite(reviewsPerPage) ||
        !Number.isFinite(totalReviews) ||
        requestedPage > totalPages ||
        reviewsPerPage < 1 ||
        totalReviews < 1
      ) {
        return cappedResponse;
      }

      const requestedOffset = (requestedPage - 1) * reviewsPerPage;
      const requestedCount = Math.min(reviewsPerPage, totalReviews - requestedOffset);
      const ascendingOffset = totalReviews - requestedOffset - requestedCount;

      if (
        requestedCount < 1 ||
        ascendingOffset < 0 ||
        ascendingOffset + requestedCount > maximumWidgetOffset
      ) {
        return cappedResponse;
      }

      const reverseSortUrl = new URL(url);
      const requestedSortDirection = reverseSortUrl.searchParams.get('sort_dir') || 'desc';
      reverseSortUrl.searchParams.set('sort_by', reverseSortUrl.searchParams.get('sort_by') || 'created_at');
      reverseSortUrl.searchParams.set('sort_dir', requestedSortDirection === 'asc' ? 'desc' : 'asc');
      reverseSortUrl.searchParams.set('per_page', '1');

      const reviewResponses = await Promise.all(
        Array.from({ length: requestedCount }, (_, index) => {
          const reviewUrl = new URL(reverseSortUrl);
          reviewUrl.searchParams.set('page', String(ascendingOffset + index + 1));
          return fetchWithUrl(reviewUrl, input, init).then((response) => {
            if (!response.ok) throw new Error('Judge.me deep pagination request failed.');
            return response.json();
          });
        }),
      );

      const requestedReviews = reviewResponses
        .map((reviewData) => {
          const responseReviews = Array.isArray(reviewData[reviewsKey])
            ? reviewData[reviewsKey]
            : reviewData.reviews;
          return responseReviews?.[0];
        })
        .filter(Boolean)
        .reverse();

      if (requestedReviews.length !== requestedCount) return cappedResponse;

      data[reviewsKey] = requestedReviews;
      if (data.pagination) data.pagination.current_page = requestedPage;
      if (data.primary_language_pagination) data.primary_language_pagination.current_page = requestedPage;

      return responseWithJson(cappedResponse, data);
    } catch (error) {
      return cappedResponse;
    }
  };
})();
